import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import clsx from "clsx";
import { useSpring, animated } from "react-spring";
import styles from "./Navigation.module.css";
import { BsHouse, BsJournals, BsJournalCheck } from "react-icons/bs";
import { IoVideocamOutline, IoCarSportSharp } from "react-icons/io5";
import { GiSettingsKnobs } from "react-icons/gi";
import { BiSolidRightArrow, BiSolidDownArrow } from "react-icons/bi";
import ReportsTree from "./ReportsTree/ReportsTree";
import { selectIsChatOpen } from "../../redux/chat/selectors";
import { useSelector } from "react-redux";
import useTooltip from "../../utils/useTooltip";
import Tooltip from "../sharedComponents/Tooltip/Tooltip";
import { LiaFileInvoiceSolid } from "react-icons/lia";
import { selectUser } from "../../redux/auth/selectors.js";
import { MdOutlinePeople, MdOutlineWarehouse } from "react-icons/md";

export default function Navigation() {
  const [isAccountingOpen, setIsAccountingOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const chatIsOpen = useSelector(selectIsChatOpen);
  const userData = useSelector(selectUser);

  const pages = userData?.access_pages ?? [];
  const hasAccess = path =>
    pages.some(item => item.page === path && item.status === 1);

  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/accounting")) {
      setIsAccountingOpen(false);
    }
    if (!location.pathname.startsWith("/reports")) {
      setIsReportsOpen(false);
    }
    if (location.pathname.startsWith("/reports")) {
      setIsReportsOpen(true);
    }
  }, [location.pathname]);

  const reportsAnimationProps = useSpring({
    maxHeight: isReportsOpen ? 300 : 0,
    opacity: isReportsOpen ? 1 : 0,
    transform: isReportsOpen ? "translateY(0)" : "translateY(-20px)",
    overflow: "hidden",
    config: {
      mass: 1,
      tension: 170,
      friction: 20,
    },
  });

  const toggleAccounting = () => {
    setIsAccountingOpen(prev => !prev);
    if (!isAccountingOpen) setIsReportsOpen(false);
  };

  const toggleReports = () => {
    setIsReportsOpen(prev => !prev);
    if (!isReportsOpen) setIsAccountingOpen(false);
  };

  const isReportsActive = location.pathname.startsWith("/reports");

  const {
    tooltipPosition,
    isTooltipVisible,
    handleMouseEnter,
    handleMouseMove,
    handleMouseLeave,
    tooltipText,
    handleClick,
  } = useTooltip();

  return (
    <nav
      className={clsx(styles.navigation, {
        [styles.navigationChatOpen]: chatIsOpen,
      })}
    >
      <ul className={styles.topNavContainer}>
        {hasAccess("/main") && (
          <li
            className={clsx(styles.navItem, styles.disabledNavItem)}
            onMouseEnter={e => handleMouseEnter(e, "Головна")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/main"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.disabled]: true,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <BsHouse className={styles.iconHome} />
              </div>

              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                })}
              >
                Головна
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {hasAccess("/video-control") && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Моніторинг")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/video-control"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <IoVideocamOutline className={styles.iconVideo} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Моніторинг
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {hasAccess("/crm") && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Планувальник")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/crm"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <BsJournals className={styles.iconCrm} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Планувальник
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {hasAccess("/accounting/clients") && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "В роботі")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/accounting/clients"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <IoCarSportSharp className={styles.iconAvto} />
                {/* <GiAutoRepair  className={styles.iconWork} /> */}
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                В роботі
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {(hasAccess("/accounting/documents/goods-and-services") ||
          hasAccess("/accounting/documents/funds")) && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Облік")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/accounting/documents"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <LiaFileInvoiceSolid className={styles.iconInvoices} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Облік
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        <li
          className={styles.navItem}
          onMouseEnter={e => handleMouseEnter(e, "Звіти")}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
        >
          <div
            onClick={toggleReports}
            className={clsx(styles.navLink, {
              [styles.active]: isReportsActive,
            })}
          >
            <div
              className={clsx(styles.iconContainer, {
                [styles.active]: isReportsActive,
                [styles.navWithChat]: chatIsOpen,
              })}
            >
              <BsJournalCheck className={styles.iconBook} />
            </div>
            <span
              className={clsx(styles.linkText, {
                [styles.hidden]: chatIsOpen,
                // [styles.visible]: !chatIsOpen
              })}
            >
              Звіти
            </span>

            {isReportsOpen ? (
              <BiSolidDownArrow
                className={clsx(styles.navArrow, {
                  [styles.smallArrow]: chatIsOpen,
                })}
              />
            ) : (
              <BiSolidRightArrow
                className={clsx(styles.navArrow, {
                  [styles.smallArrow]: chatIsOpen,
                })}
              />
            )}
          </div>
          <animated.div
            style={reportsAnimationProps}
            className={clsx(styles.accountingTreeContainer, {
              [styles.open]: isAccountingOpen,
            })}
          >
            <ReportsTree
              closeTree={() => setIsReportsOpen(false)}
              hasAccess={hasAccess}
            />
          </animated.div>
          <Tooltip
            isVisible={isTooltipVisible && chatIsOpen}
            position={tooltipPosition}
            text={tooltipText}
          />
        </li>
        {hasAccess("/staff") && ( 
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Персонал")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/staff"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <MdOutlinePeople className={styles.iconSettings} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Персонал
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {hasAccess("/warehouse") && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Склад")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/warehouse"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <MdOutlineWarehouse className={styles.iconSettings} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Склад
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
        {hasAccess("/settings") && (
          <li
            className={styles.navItem}
            onMouseEnter={e => handleMouseEnter(e, "Налаштування")}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
          >
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                clsx(styles.navLink, {
                  [styles.active]: isActive,
                  [styles.navWithChat]: chatIsOpen,
                })
              }
            >
              <div className={styles.iconContainer}>
                <GiSettingsKnobs className={styles.iconSettings} />
              </div>
              <span
                className={clsx(styles.linkText, {
                  [styles.hidden]: chatIsOpen,
                  // [styles.visible]: !chatIsOpen
                })}
              >
                Налаштування
              </span>
            </NavLink>
            <Tooltip
              isVisible={isTooltipVisible && chatIsOpen}
              position={tooltipPosition}
              text={tooltipText}
            />
          </li>
        )}
      </ul>
    </nav>
  );
}
